import React, { useState } from 'react';
import VegaIcon from './vegaIcons';
import { SOFTWARE_CATALOG, ROLE_TONE, SECTOR_MAPS } from './vegaData';

// Dev-portal colour palette (matches the live portal aesthetic)
const C = {
  ink:        '#0a0b10',
  ink2:       '#25272f',
  ink3:       '#4a4d57',
  muted:      '#7a7d87',
  faint:      '#aeb1ba',
  paper:      '#faf9f5',
  paper2:     '#f1efe8',
  line:       'rgba(10,11,16,.10)',
  line2:      'rgba(10,11,16,.06)',
  accent:     '#1f3dd6',
  accentSoft: 'rgba(31,61,214,.08)',
  ok:         '#1f7a4d',
  okSoft:     'rgba(31,122,77,.10)',
};

function ClusterMini({ nodes, accent }) {
  const cols = nodes >= 5 ? 3 : nodes >= 3 ? 3 : 2;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '6px' }}>
      {Array.from({ length: nodes }).map((_, i) => (
        <div key={i} style={{ width: 12, height: 12, borderRadius: 3, background: accent, opacity: 0.8 }} />
      ))}
    </div>
  );
}

export default function VegaDeployment() {
  const [category, setCategory] = useState('database');
  const [targets, setTargets] = useState(['dev', 'prod']);

  const cat = SOFTWARE_CATALOG.find(c => c.id === category);
  const sharedCount = cat.deps.filter(d => d.shared).length;
  const totalDeps = cat.deps.length;
  const clusterCount = targets.reduce((n, id) => n + SECTOR_MAPS[id].clusters.length, 0);
  const newDeps = totalDeps - sharedCount;

  const toggleTarget = id =>
    setTargets(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  return (
    <section
      id="deploy"
      className="vd-section-y px-6 lg:px-10"
      style={{ borderTop: '1px solid var(--line-soft)' }}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ maxWidth: '42rem' }}>
          <div className="vd-eyebrow" style={{ marginBottom: '1.25rem' }}>Deployment · the terrain</div>
          <h2
            className="vd-font-display vd-ink"
            style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)', lineHeight: 1.08, textWrap: 'balance' }}
          >
            Customize the deployment <br />
            <span style={{ fontStyle: 'italic' }}>to fit the terrain.</span>
          </h2>
          <p className="vd-ink-soft" style={{ marginTop: '1.5rem', maxWidth: '36rem', lineHeight: 1.7 }}>
            Pick a category. Vega resolves its dependency graph — sharing what's already running —
            then reconciles to every sector you target.
          </p>
        </div>

        {/* Card */}
        <div
          style={{
            marginTop: '2.5rem',
            borderRadius: '1.5rem',
            padding: 'clamp(1.5rem, 4vw, 2rem)',
            background: C.paper,
            border: `1px solid ${C.line}`,
            fontFamily: '"Inter Tight", system-ui, sans-serif',
          }}
        >
          {/* Category pills + resolve preview */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '1.5rem' }}>
            <div style={{
              display: 'inline-flex',
              flexWrap: 'nowrap',
              gap: '0.375rem',
              padding: '0.25rem',
              borderRadius: '999px',
              border: `1px solid ${C.line}`,
              background: '#fff',
            }}>
              {SOFTWARE_CATALOG.map(c => (
                <button
                  key={c.id}
                  onClick={() => setCategory(c.id)}
                  style={{
                    padding: '0.375rem 1rem',
                    borderRadius: '999px',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    border: 'none',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.15s',
                    background: category === c.id ? C.ink : 'transparent',
                    color: category === c.id ? '#fff' : C.ink3,
                    boxShadow: category === c.id ? '0 1px 2px rgba(10,11,16,.15)' : 'none',
                  }}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <div style={{ fontSize: '0.6875rem', fontFamily: '"JetBrains Mono", monospace', color: C.muted, whiteSpace: 'nowrap' }}>
              vega resolve{' '}
              <span style={{ color: C.accent }}>{cat.primary.name}</span>
              <span style={{ color: C.faint }}> → </span>
              <span style={{ color: C.ok }}>{targets.length} sector{targets.length === 1 ? '' : 's'}</span>
            </div>
          </div>

          {/* Two-column layout */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr)', gap: '1.5rem' }}
               className="lg:grid-cols-2 grid-cols-1">

            {/* LEFT: dependency tree */}
            <div style={{ borderRadius: '1rem', border: `1px solid ${C.line}`, background: '#fff', overflow: 'hidden', boxShadow: '0 1px 2px rgba(10,11,16,.04)' }}>
              {/* Primary atlas header */}
              <div style={{ padding: '1rem 1.25rem', borderBottom: `1px solid ${C.line2}`, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700, color: '#fff',
                  background: 'linear-gradient(135deg, #2a8fe7, #1f3dd6)',
                }}>
                  {cat.primary.name.slice(0, 2).toUpperCase()}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.875rem', fontWeight: 500, color: C.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {cat.primary.name}
                    </div>
                    <span style={{ fontSize: '0.625rem', padding: '0.125rem 0.375rem', borderRadius: 999, fontFamily: '"JetBrains Mono", monospace', background: C.accentSoft, color: C.accent }}>
                      {cat.primary.v}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.6875rem', marginTop: 2, color: C.muted }}>{cat.primary.tag}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontFamily: '"Instrument Serif", serif', fontStyle: 'italic', fontSize: 26, lineHeight: 1, color: C.ink }}>
                    {totalDeps}
                  </div>
                  <div style={{ fontSize: '0.5625rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: C.muted }}>
                    deps resolved
                  </div>
                </div>
              </div>

              {/* Dependency list */}
              <div style={{ padding: '0.75rem 1.25rem' }}>
                <div style={{ fontSize: '0.625rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: C.faint, marginBottom: '0.5rem' }}>
                  Dependency graph
                </div>
                <div style={{ position: 'relative' }}>
                  {/* Vertical guide */}
                  <div style={{ position: 'absolute', left: 7, top: 8, bottom: 8, width: 1, background: C.line2 }} />

                  {cat.deps.map((dep, i) => {
                    const tone = ROLE_TONE[dep.role] || ROLE_TONE.tls;
                    return (
                      <div key={dep.name} style={{ position: 'relative', paddingLeft: '1.5rem', paddingTop: '0.375rem', paddingBottom: '0.375rem' }}>
                        {/* Elbow connector */}
                        <div style={{ position: 'absolute', left: 7, top: 14, width: 12, height: 1, background: C.line2 }} />
                        {/* Node dot */}
                        <div style={{
                          position: 'absolute', left: 4, top: 10,
                          width: 8, height: 8, borderRadius: '50%',
                          background: dep.shared ? C.ok : C.accent,
                          boxShadow: `0 0 0 3px ${dep.shared ? C.okSoft : C.accentSoft}`,
                        }} />
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                            <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.78125rem', color: C.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {dep.name}
                            </span>
                            <span style={{ fontSize: '0.625rem', fontFamily: '"JetBrains Mono", monospace', color: C.faint, flexShrink: 0 }}>{dep.v}</span>
                            <span style={{ padding: '0.125rem 0.375rem', borderRadius: 999, fontSize: '0.59375rem', fontWeight: 500, flexShrink: 0, background: tone.bg, color: tone.fg }}>
                              {dep.role}
                            </span>
                          </div>
                          <div style={{ flexShrink: 0 }}>
                            {dep.shared ? (
                              <span style={{ fontSize: '0.625rem', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0.125rem 0.375rem', borderRadius: 999, background: C.okSoft, color: C.ok }}>
                                <span style={{ width: 4, height: 4, borderRadius: '50%', background: C.ok }} />
                                shared · reused
                              </span>
                            ) : (
                              <span style={{ fontSize: '0.625rem', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0.125rem 0.375rem', borderRadius: 999, background: C.accentSoft, color: C.accent }}>
                                <span style={{ width: 4, height: 4, borderRadius: '50%', background: C.accent }} />
                                new install
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Resolution note */}
              <div style={{ padding: '0.75rem 1.25rem', borderTop: `1px solid ${C.line2}`, background: C.paper2, display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                  <circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" />
                </svg>
                <div style={{ fontSize: '0.6875rem', lineHeight: 1.5, color: C.ink3 }}>
                  <span style={{ fontWeight: 600, color: C.ink }}>{sharedCount} of {totalDeps} dependencies already resolved</span>
                  {' · '}{cat.note}
                </div>
              </div>
            </div>

            {/* RIGHT: multi-target sectors */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <div style={{ fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: C.muted }}>
                  Target sectors
                </div>
                <div style={{ fontSize: '0.625rem', color: C.muted }}>
                  multi-select · {targets.length} chosen
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                {Object.entries(SECTOR_MAPS).map(([id, s]) => {
                  const checked = targets.includes(id);
                  return (
                    <button
                      key={id}
                      onClick={() => toggleTarget(id)}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        borderRadius: '0.75rem',
                        border: `1px solid ${checked ? s.accent : C.line}`,
                        padding: '0.75rem',
                        background: checked ? '#fff' : 'rgba(255,255,255,.55)',
                        boxShadow: checked ? `0 0 0 1px ${s.accent}, 0 1px 2px rgba(10,11,16,.06)` : 'none',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        {/* Checkbox */}
                        <div style={{
                          width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: checked ? s.accent : '#fff',
                          border: `1.5px solid ${checked ? s.accent : C.line}`,
                        }}>
                          {checked && (
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M5 12.5l4.5 4.5L19 7" />
                            </svg>
                          )}
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.12em', padding: '0.125rem 0.5rem', borderRadius: 999, background: s.soft, color: s.accent }}>
                              {s.pill}
                            </span>
                            <span style={{ fontSize: '0.625rem', fontFamily: '"JetBrains Mono", monospace', color: C.faint }}>
                              {s.clusters.length} cluster{s.clusters.length === 1 ? '' : 's'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Mini cluster row */}
                      <div style={{ marginTop: '0.75rem', marginLeft: '1.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {s.clusters.map(c => (
                          <div key={c.name} style={{
                            borderRadius: 6,
                            padding: '0.375rem 0.5rem',
                            background: checked ? s.soft : C.paper2,
                            border: `1px dashed ${checked ? s.accent : 'transparent'}`,
                          }}>
                            <div style={{ fontSize: '0.5625rem', fontFamily: '"JetBrains Mono", monospace', marginBottom: 4, color: checked ? s.accent : C.muted }}>
                              {c.name}
                            </div>
                            <ClusterMini nodes={c.nodes} accent={checked ? s.accent : C.faint} />
                          </div>
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Reconcile bar */}
              <div style={{
                marginTop: '1rem',
                borderRadius: '0.75rem',
                border: `1px solid ${C.line}`,
                padding: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '0.75rem',
                background: '#fff',
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: '"Instrument Serif", serif', fontStyle: 'italic', fontSize: 22, lineHeight: 1, color: C.ink }}>
                    {clusterCount} cluster{clusterCount === 1 ? '' : 's'}
                  </div>
                  <div style={{ fontSize: '0.65625rem', marginTop: 2, color: C.muted }}>
                    will receive{' '}
                    <span style={{ fontFamily: '"JetBrains Mono", monospace', color: C.ink3 }}>{cat.primary.name}</span>
                    {' '}+ {newDeps} new dep{newDeps === 1 ? '' : 's'}
                  </div>
                </div>
                <button
                  className="vd-btn-primary"
                  disabled={targets.length === 0}
                  style={targets.length === 0 ? { opacity: 0.4, cursor: 'not-allowed', fontSize: '0.875rem' } : { fontSize: '0.875rem' }}
                >
                  Reconcile <VegaIcon name="arrow" className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
